import React from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { linkIdentity, searchContacts, type ContactSummary } from '../api/contacts'

interface LinkIdentityModalProps {
  open: boolean
  stationId: number
  identityId: number | null
  onClose: () => void
  onLinked?: (contact: ContactSummary) => void
}

const LinkIdentityModal: React.FC<LinkIdentityModalProps> = ({ open, stationId, identityId, onClose, onLinked }) => {
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState<ContactSummary[]>([])
  const [selected, setSelected] = React.useState<ContactSummary | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const fetchContacts = React.useCallback(
    async (value: string) => {
      try {
        setLoading(true)
        const response = await searchContacts(stationId, value)
        setResults(response.items)
        if (response.items.length === 1) {
          setSelected(response.items[0])
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to search contacts.'
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    [stationId],
  )

  React.useEffect(() => {
    if (!open) {
      return
    }
    setError(null)
    setSelected(null)
    void fetchContacts('')
  }, [open, fetchContacts])

  const handleSubmit = async () => {
    if (!identityId || !selected) {
      return
    }

    try {
      setLoading(true)
      await linkIdentity(stationId, selected.id, identityId)
      onLinked?.(selected)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to link identity.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)
    void fetchContacts(value)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Link identity to a contact</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Search contacts"
            placeholder="Search by name or location"
            value={query}
            onChange={handleSearchChange}
            fullWidth
            autoFocus
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <Divider />
          <List dense sx={{ maxHeight: 240, overflowY: 'auto' }}>
            {results.map((contact) => {
              const identities = contact.identities.map((identity) => identity.display_name || identity.platform).join(', ')
              const isSelected = selected?.id === contact.id

              return (
                <ListItemButton key={contact.id} selected={isSelected} onClick={() => setSelected(contact)}>
                  <ListItemText
                    primary={contact.name || '(unnamed contact)'}
                    secondary={
                      <>
                        <Typography component="span" variant="caption" color="text.secondary">
                          {contact.location || 'No location specified'}
                        </Typography>
                        <br />
                        <Typography component="span" variant="caption" color="text.secondary">
                          Identities: {identities || 'None'}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              )
            })}
            {!loading && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No contacts found. Try a different search term.
              </Typography>
            )}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!selected || loading || !identityId}>
          Link to contact
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LinkIdentityModal

